import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Users, UserCheck, HelpCircle, ArrowRight } from "lucide-react";
import api from "../../lib/api";

const STATUS_LABELS = {
  new: "Yeni",
  reviewing: "İnceleniyor",
  contacted: "İletişime Geçildi",
  accepted: "Kabul Edildi",
  rejected: "Reddedildi",
};

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const cards = [
    { icon: FileText, label: "Toplam Başvuru", value: data?.total_applications ?? "—", testid: "overview-total-applications" },
    { icon: FileText, label: "Yeni Başvuru", value: data?.new_applications ?? "—", testid: "overview-new-applications", highlight: true },
    { icon: Users, label: "Toplam Yayıncı", value: data?.total_publishers ?? "—", testid: "overview-total-publishers" },
    { icon: UserCheck, label: "Aktif Yayıncı", value: data?.active_publishers ?? "—", testid: "overview-active-publishers" },
    { icon: HelpCircle, label: "SSS Sayısı", value: data?.total_faqs ?? "—", testid: "overview-total-faqs" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display font-700 text-xl text-white mb-1">Genel Bakış</h1>
      <p className="text-sm text-[#64748B] mb-8">Sitenizin güncel durumu</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div
            key={c.label}
            data-testid={c.testid}
            className={`card-lift rounded-2xl border p-5 ${
              c.highlight ? "border-cyan-400/30 bg-gradient-to-br from-[#0047FF]/15 to-[#00F0FF]/10" : "border-white/[0.06] bg-[#0A0E17]"
            }`}
          >
            <c.icon size={19} strokeWidth={1.5} className="text-cyan-400 mb-4" />
            <div className="font-display font-700 text-2xl text-white">{c.value}</div>
            <div className="text-xs text-[#A0ABC0] mt-1.5">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-2xl border border-white/[0.06] bg-[#0A0E17] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <h2 className="text-sm font-semibold text-white">Son Başvurular</h2>
          <Link to="/admin/basvurular" data-testid="overview-view-all-link" className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 hover:text-cyan-100">
            Tümünü Gör <ArrowRight size={13} />
          </Link>
        </div>
        {!data || data.recent_applications.length === 0 ? (
          <p className="px-6 py-10 text-sm text-[#64748B] text-center" data-testid="overview-no-applications">Henüz başvuru bulunmuyor.</p>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {data.recent_applications.map((a) => (
              <div key={a.id} className="px-6 py-4 flex items-center justify-between gap-4" data-testid={`overview-application-${a.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{a.full_name}</p>
                  <p className="text-xs text-[#64748B] truncate">{a.tiktok_username}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-[11px] font-semibold text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-2.5 py-1">
                    {STATUS_LABELS[a.status] || a.status}
                  </span>
                  <p className="text-[11px] text-[#64748B] mt-1.5">{new Date(a.created_at).toLocaleDateString("tr-TR")}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10 rounded-2xl border border-white/[0.06] bg-[#0A0E17] overflow-hidden">
        <div className="px-6 py-4 border-b border-white/[0.06]">
          <h2 className="text-sm font-semibold text-white">Son Ziyaretçiler</h2>
        </div>
        {!data || data.recent_visitors.length === 0 ? (
          <p className="px-6 py-10 text-sm text-[#64748B] text-center" data-testid="overview-no-visitors">Henüz ziyaretçi kaydı yok.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left" data-testid="visitors-table">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {["IP Adresi", "Adres", "Tarayıcı", "Zaman"].map((h) => (
                    <th key={h} className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {data.recent_visitors.map((v) => (
                  <tr key={v.id} data-testid={`visitor-row-${v.id}`}>
                    <td className="px-6 py-3.5 text-xs font-semibold text-cyan-300">{v.ip}</td>
                    <td className="px-6 py-3.5 text-xs text-white">{v.path}</td>
                    <td className="px-6 py-3.5 text-xs text-[#64748B] max-w-[260px] truncate">{v.user_agent}</td>
                    <td className="px-6 py-3.5 text-xs text-[#A0ABC0]">{new Date(v.created_at).toLocaleString("tr-TR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
